// feeds.ts — the feed vocabulary shared by the overview pane and its charts.
//
// A "feed" is a run that streams per-step curves (the `runs` + `run_points`
// tables). Each selected commit can carry several, so the pane flattens them
// into one list and gives each a color slot; the chart components only ever
// see that flattened form.
import type { CommitOverview, Run } from '../api';
import { shortSha } from './format';

export interface FeedRef {
  run: Run;
  /** The selected commit this feed hangs off. */
  sha: string;
  /** Categorical color slot, assigned by selection order and never reused. */
  slot: number;
}

// /curves takes at most 50 run ids per request, and 20 selected commits can
// carry more feeds than that between them, so the flattened list is capped
// and the pane says when it truncated.
export const MAX_FEEDS = 50;

export function flattenFeeds(commits: CommitOverview[]): FeedRef[] {
  const feeds: FeedRef[] = [];
  for (const c of commits) {
    for (const run of c.feeds ?? []) {
      feeds.push({ run, sha: c.commit.sha, slot: feeds.length });
    }
  }
  return feeds.slice(0, MAX_FEEDS);
}

export function feedLabel(f: FeedRef): string {
  return `${shortSha(f.run.sha || f.sha)}${f.run.name ? ` \u00b7 ${f.run.name}` : ''}`;
}

// TensorBoard-style debiased EMA, so early steps aren't dragged toward 0.
export function smoothValues(points: [number, number][], weight: number): number[] {
  if (weight <= 0) return points.map((p) => p[1]);
  let last = 0;
  return points.map(([, v], i) => {
    last = last * weight + (1 - weight) * v;
    return last / (1 - Math.pow(weight, i + 1));
  });
}

// Index of the point nearest `step` in a step-sorted curve (tooltip lookup).
export function nearest(points: [number, number][], step: number): number | undefined {
  let lo = 0;
  let hi = points.length - 1;
  if (hi < 0) return undefined;
  while (lo < hi) {
    const mid = (lo + hi) >> 1;
    if (points[mid][0] < step) lo = mid + 1;
    else hi = mid;
  }
  if (lo > 0 && Math.abs(points[lo - 1][0] - step) < Math.abs(points[lo][0] - step)) lo--;
  return lo;
}
